// TODO: cleanup with research_assistant_gen_3 ff
import { Box, Skeleton, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getProjectVariablePath } from '@verantos-dev/core-constants';
import {
  AvSlugClickProvider,
  CodesTab,
  ConceptsTab,
  DecisionsTab,
  DefinitionTab,
  DescriptionTab,
  ResultsTab,
  SqlTab,
  UsageTab,
  VerificationTab,
  resolveActiveTab,
  tabLabel,
  tabsForArtifact,
  type EpiArtifact,
  type EpiArtifactTab,
} from '@verantos-dev/feature-epi-chat';
import { SubPageBreadcrumbHeader } from '@verantos-dev/shared-components';

import { useActiveChatContext } from '../context/ActiveChatContext';
import { useVariableDraftBreadcrumbs } from '../hooks/useVariableDraftBreadcrumbs';

// OR-6115 shape: an inline AV reference hands back a bare slug, an
// already-qualified knowledge id (``av.<slug>``), or a locked-library UUID.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Inline draft surface for an in-flight analytical variable (OR-5963 step 6).
 *
 * Mounted at ``/projects/:projectId/draft/variable/:clientId`` whenever
 * the agent emits ``STATUS_UPDATE { kind: 'focus_scope', scope: {
 * kind: 'variable_draft', ... } }`` and ``useFocusScopeNavigator`` drives
 * the URL here. The page reads ``page.artifacts`` from the shared
 * {@link ActiveChatProvider} (mounted by ``ProjectPage`` when ``?chat=``
 * is set) and renders the artifact whose ``slug`` matches the
 * ``remoteFocusScope``'s ``slug`` — the same tabbed view the legacy
 * artifact drawer uses, but inline in the right pane.
 *
 * Lookup contract:
 *   - URL carries ``clientId`` (server-minted UUID).
 *   - ``remoteFocusScope`` carries both ``client_id`` AND the artifact's
 *     ``slug`` (the BE's ``draft_analytical_variable`` wrapper emits both — see
 *     ``orchestrator._draft_analytical_variable``).
 *   - ``EpiArtifact`` is keyed on ``slug`` (the BE manifest's identifier).
 *   - So: confirm the focus scope's ``client_id`` matches the URL, read
 *     its ``slug``, find the artifact by slug.
 *
 * Edge cases:
 *   - Right after the focus_scope event fires the artifact may not yet
 *     exist in ``page.artifacts`` (the AV stream hasn't started). Render
 *     a "Setting up…" skeleton.
 *   - Slug is optional on the wire (per ``VariableDraftScopeWireSchema``).
 *     For the auto-emit path the orchestrator always populates it; if it
 *     ever lands without one we fall back to a slug-less skeleton.
 *   - No chat is open (``ctx === null``) — the user hit the draft URL
 *     directly without an active chat. Show empty-state copy.
 */
export const ProjectVariableDraftPage = () => {
  const theme = useTheme();
  const { clientId: urlClientId = '', projectId = '' } = useParams<{
    clientId: string;
    projectId: string;
  }>();
  const navigate = useNavigate();
  const ctx = useActiveChatContext();

  // OR-6306: an inline AV-slug reference in the draft's Description/SQL tabs
  // should route to the referenced variable's detail view instead of
  // falling back to the legacy knowledge-browser dialog. This surface is
  // never cohort-scoped (route is flat under the project), so always the
  // project-scoped path.
  const handleAvSlugClick = useCallback(
    (idOrSlug: string) => {
      if (!projectId) return;
      const knowledgeId =
        idOrSlug.startsWith('av.') || UUID_PATTERN.test(idOrSlug) ? idOrSlug : `av.${idOrSlug}`;
      navigate(getProjectVariablePath(projectId, knowledgeId));
    },
    [navigate, projectId]
  );

  // Local tab selection. The drawer keeps tab state in the per-session
  // reducer so a streaming tool emit (verify_analytical_variable → Verification) can
  // snap-to-tab; this surface is short-lived enough that a per-mount
  // default + local state is fine, and avoids coupling to chat actions.
  const [activeTab, setActiveTab] = useState<EpiArtifactTab | null>(null);

  // Match URL ``clientId`` against the latest focus_scope event. A stale
  // scope (e.g. an old draft superseded by a fresh emit on a different
  // ``client_id``) shouldn't render the wrong artifact's body — fall
  // back to the skeleton rather than pick an arbitrary artifact.
  const remoteFocusScope = ctx?.page.remoteFocusScope ?? null;
  const scopeMatchesUrl =
    remoteFocusScope?.kind === 'variable_draft' && remoteFocusScope.client_id === urlClientId;
  const slug = scopeMatchesUrl ? remoteFocusScope.slug : undefined;
  const title = scopeMatchesUrl ? remoteFocusScope.title : undefined;

  const artifacts = ctx?.page.artifacts ?? [];
  const runState = ctx?.page.runState ?? 'idle';

  const artifact: EpiArtifact | null = useMemo(() => {
    if (!slug) return null;
    return artifacts.find((a) => a.name === slug) ?? null;
  }, [artifacts, slug]);

  // Reset tab selection when the artifact identity flips (a different
  // draft replaces the current one). Content-only updates on the same
  // slug preserve the user's tab choice.
  useEffect(() => {
    setActiveTab(null);
  }, [artifact?.name]);

  const tabs = useMemo(() => tabsForArtifact(artifact), [artifact]);
  const resolvedTab = useMemo(() => resolveActiveTab(artifact, activeTab), [artifact, activeTab]);

  const onJumpToSqlLine = useCallback((_line: number) => {
    // Switching tabs is enough for now — the SyntaxHighlighter doesn't
    // expose per-line scroll. Mirrors ``ArtifactDrawer``. The line
    // argument is part of the ``VerificationTab`` contract but unused
    // until the renderer exposes per-line scroll.
    void _line;
    setActiveTab('sql');
  }, []);

  // No active chat — there's no artifact stream to read from. Land on a
  // helpful empty state rather than a blank pane. (Hitting this URL
  // without ``?chat=`` is an edge case — the navigator only routes here
  // when a chat is mounted — but a deep-link or refresh can land here.)
  if (!ctx) {
    return (
      <Box
        data-testid="project-variable-draft-page"
        sx={{ paddingX: 6, paddingY: 3, display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        <Typography variant="subtitle1">Working on a new variable…</Typography>
        <Typography variant="body2" color="text.secondary">
          Open the chat that started this draft to see it live.
        </Typography>
      </Box>
    );
  }

  const headerTitle = title?.trim() || slug || 'Working on a new variable…';
  const statusLabel = runState === 'streaming' ? 'Working…' : 'Draft';

  return (
    <ProjectVariableDraftPageShell headerTitle={headerTitle} statusLabel={statusLabel}>
      {artifact ? (
        <>
          {tabs.length > 0 && (
            <Tabs
              value={resolvedTab}
              onChange={(_, value) => setActiveTab(value as EpiArtifactTab)}
              variant="scrollable"
              scrollButtons={false}
              sx={{
                minHeight: 36,
                borderBottom: `1px solid ${theme.palette.divider}`,
                '& .MuiTab-root': {
                  minHeight: 36,
                  paddingY: 0.5,
                  textTransform: 'none',
                  fontSize: theme.typography.body2.fontSize,
                },
              }}
              data-testid="project-variable-draft-tabs"
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab}
                  value={tab}
                  label={tabLabel(tab)}
                  data-testid={`project-variable-draft-tab-${tab}`}
                />
              ))}
            </Tabs>
          )}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <AvSlugClickProvider onClick={handleAvSlugClick}>
              {renderTabBody(artifact, resolvedTab, onJumpToSqlLine)}
            </AvSlugClickProvider>
          </Box>
        </>
      ) : (
        <Box sx={{ padding: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            {slug ? `Setting up ${slug}…` : 'Setting up the new variable…'}
          </Typography>
          <Skeleton variant="rectangular" height={32} />
          <Skeleton variant="rectangular" height={120} />
        </Box>
      )}
    </ProjectVariableDraftPageShell>
  );
};

/**
 * Page shell for the variable-draft surface: the F8 breadcrumb header (title +
 * status pill) on top, then the tab content below.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
const ProjectVariableDraftPageShell = ({
  headerTitle,
  statusLabel,
  children,
}: {
  headerTitle: string;
  statusLabel: string;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  const { breadcrumbs } = useVariableDraftBreadcrumbs({ draftTitle: headerTitle });

  return (
    <Box
      data-testid="project-variable-draft-page"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      }}
    >
      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
        <SubPageBreadcrumbHeader
          id="project-variable-draft-header"
          breadcrumbs={breadcrumbs}
          title={headerTitle}
          rightContent={
            <Box
              component="span"
              sx={{
                paddingX: 1,
                paddingY: 0.25,
                borderRadius: 1,
                backgroundColor:
                  statusLabel === 'Working…'
                    ? theme.palette.info.light
                    : theme.palette.action.hover,
                color:
                  statusLabel === 'Working…'
                    ? theme.palette.info.contrastText
                    : theme.palette.text.secondary,
                fontSize: theme.typography.caption.fontSize,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
              data-testid="project-variable-draft-status"
            >
              {statusLabel}
            </Box>
          }
        />
      </Box>
      {children}
    </Box>
  );
};

const renderTabBody = (
  artifact: EpiArtifact,
  tab: EpiArtifactTab,
  onJumpToSqlLine: (line: number) => void
) => {
  switch (tab) {
    case 'description':
      return <DescriptionTab artifact={artifact} />;
    case 'sql':
      return <SqlTab artifact={artifact} />;
    case 'verification':
      return <VerificationTab artifact={artifact} onJumpToSqlLine={onJumpToSqlLine} />;
    case 'results':
      return <ResultsTab artifact={artifact} />;
    case 'definition':
      return <DefinitionTab artifact={artifact} />;
    case 'codes':
      return <CodesTab artifact={artifact} />;
    case 'concepts':
      return <ConceptsTab artifact={artifact} />;
    case 'decisions':
      return <DecisionsTab artifact={artifact} />;
    case 'usage':
      return <UsageTab artifact={artifact} />;
  }
};
